import type { App } from '@keboola/api-client/developerPortal/types';

import { hasIcon } from './helpers';

/**
 * Direction prefixes on a component id; stripping them collapses `ex-abra-flexi` and
 * `wr-abra-flexi` (one service, one logo) onto `abra-flexi`.
 */
const DIRECTION_MARKER = /^(ex|wr|app|pattern|dataapp)-/;

/** The service a component id is about, with vendor and direction removed. */
const serviceOf = (app: App): string => {
  let tail = app.id.includes('.') ? app.id.slice(app.id.indexOf('.') + 1) : app.id;
  let previous = '';
  // Repeated: some ids stack markers.
  while (previous !== tail) {
    previous = tail;
    tail = tail.replace(DIRECTION_MARKER, '');
  }
  return tail;
};

/**
 * The components whose icons make up the homepage's band, each a distinct picture.
 *
 * 1. No processors — they all wear the house logo.
 * 2. One per service — icon URLs are all unique strings, so dedupe on the id stem.
 * 3. An even stride, not the first N — the alphabetical head is near-identical logos.
 *    Deterministic, so the visual baseline reproduces.
 *
 * Expects the already-filtered catalog, so the caller's count agrees with the band.
 */
export const pickWallIcons = (catalog: App[], size: number): App[] => {
  const eligible = catalog.filter((app) => app.type !== 'processor' && hasIcon(app));

  const seen = new Set<string>();
  const oncePerService = eligible.filter((app) => {
    const service = serviceOf(app);
    if (seen.has(service)) return false;
    seen.add(service);
    return true;
  });

  const stride = Math.max(1, Math.floor(oncePerService.length / size));
  return oncePerService.filter((_, index) => index % stride === 0).slice(0, size);
};
